import { Fragment } from 'react';

import { CheckboxField } from '@zola/zola-ui/src/components/Form/CheckboxField';

import cx from 'classnames';
import some from 'lodash/some';

import {
  vendorSearchSortingToString,
  parseSortingValues,
} from '~/pages/couples/explore/components/SearchResults/components/ManualSortingOptions/ManualSortingOptions';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { createVendorSearchFromFilters } from '~/pages/couples/explore/util/createVendorSearchFromFilters';
import { OptionFacetCategoryType } from '~/types/types';

import { useVendorSearchNavigation } from '../../../../contexts/VendorSearchNavigationContext';
import { isSortingFilter } from '../FacetButton/FacetButton';
import PriceFacetForm from '../PriceFacetForm/PriceFacetForm';
import { AvailabilityFilterDayPicker } from '../SearchFacets/AvailabilityFilterDayPicker';
import { FacetSideDrawerRadio } from './FacetSideDrawer.styles';

import styles from './FacetSideDrawer.module.less';

interface FacetDrawerItemProps {
  category: OptionFacetCategoryType;
}

export function FacetDrawerItem({ category }: FacetDrawerItemProps): JSX.Element {
  const { vendorSearch, selectedFacetOptions } = useSearchResultsPageContext();
  const { navigateToSrp } = useVendorSearchNavigation();

  if (category.key === 'price') {
    return (
      <div>
        <PriceFacetForm showSubmit={false} />
      </div>
    );
  }
  if (category.key === 'availability') {
    return (
      <div className="facet-button__container">
        <AvailabilityFilterDayPicker classNames={{ caption: styles.calendarCaption }} />
      </div>
    );
  }
  if (isSortingFilter(category) || category.key === 'planner-service-level') {
    return (
      <Fragment>
        {category.facets?.map((childFacet) => (
          <FacetSideDrawerRadio
            id={childFacet.key}
            key={childFacet.key}
            checked={
              childFacet.key === vendorSearchSortingToString(vendorSearch) ||
              some(selectedFacetOptions, (tempOption) => tempOption.slug === childFacet.slug)
            }
            label={
              <div className={styles.nameLabel}>{childFacet.displayName || childFacet.name}</div>
            }
            onChange={() => {
              if (isSortingFilter(category)) {
                const [sortBy, sortOrder] = parseSortingValues(childFacet.slug);
                navigateToSrp({ sorting: { sortBy, sortOrder } });
              }
              if (category.key === 'planner-service-level') {
                const next = selectedFacetOptions.filter(
                  (option) => option.parentKey !== category.key
                );
                navigateToSrp(createVendorSearchFromFilters([...next, childFacet]));
              }
            }}
          />
        ))}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {category.facets?.map((childFacet) => {
        const isChecked = some(
          selectedFacetOptions,
          (tempOption) => tempOption.slug === childFacet.slug
        );
        const toggleCheckbox = () => {
          if (!isChecked) {
            navigateToSrp(createVendorSearchFromFilters([...selectedFacetOptions, childFacet]));
          } else {
            navigateToSrp(
              createVendorSearchFromFilters(
                selectedFacetOptions.filter((f) => f.slug !== childFacet.slug)
              )
            );
          }
        };

        const classNames = cx(styles.pillCheck, {
          active: isChecked,
          'with-flavor-text': !!childFacet.flavorText,
        });
        return (
          <CheckboxField
            className={classNames}
            onChange={toggleCheckbox}
            checked={isChecked}
            key={`id-${category.slug}-${childFacet.slug}`}
            id={`id-${category.slug}-${childFacet.slug}`}
            label={
              <Fragment>
                {childFacet.flavorText && (
                  <div className="pill-check__flavor-text">{childFacet.flavorText}</div>
                )}
                <div className={styles.nameLabel}>{childFacet.displayName || childFacet.name}</div>
                {childFacet.description ? (
                  <div className={styles.descriptionLabel}>{childFacet.description}</div>
                ) : null}
              </Fragment>
            }
          />
        );
      })}
    </Fragment>
  );
}
